import { AuthProvider } from './contexts/AuthContext';
import { EnvProvider, useEnv } from './contexts/EnvContext';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
// Import chart configuration to ensure it's loaded before any components
import './chartConfig';
// Fonts
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme, { createAppTheme } from './theme';
import { applyChartTheme } from './chartConfig';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const ThemeModeContext = React.createContext({ mode: 'light', toggle: () => {} });

function ThemeModeProvider({ children }) {
  const [mode, setMode] = React.useState(() => localStorage.getItem('themeMode') || 'light');
  const { env } = useEnv();
  const muiTheme = React.useMemo(() => createAppTheme(mode, env), [mode, env]);
  React.useEffect(() => { applyChartTheme(muiTheme); }, [muiTheme]);
  const value = React.useMemo(() => ({
    mode,
    toggle: () => setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    })
  }), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export const useThemeMode = () => React.useContext(ThemeModeContext);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <EnvProvider>
    <ThemeModeProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ThemeModeProvider>
  </EnvProvider>
);
